import React, { FC, useEffect, useMemo, useState } from 'react';
import Header from '../../components/header';
import {
  Alert,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '../../reducers';
import { CompanySelectionActionCreator } from '../../state_management/actions/CompanySelectionActionCreator';
import { companyQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';
import { CompanyDto } from '@thingslog/repositories';
import DeviceSearchTable from './DeviceSearchTable';

const DEVICE_SEARCH_DEBOUNCE_MS = 500;

const DeviceSearch: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedCompany = useSelector((state: ReduxState) => state.company);
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedDeviceNumber, setDebouncedDeviceNumber] = useState<string>('');

  const normalizedDeviceNumber = useMemo(() => searchInput.trim(), [searchInput]);
  const hasSearchValue = normalizedDeviceNumber.length > 0;
  const isSearchReady = debouncedDeviceNumber.length > 0;
  const emptyPrompt = t('device_search_empty_prompt') || 'Enter a device number to search.';

  const updateDebouncedDeviceNumber = useMemo(
    () =>
      _.debounce((value: string): void => {
        setDebouncedDeviceNumber(value);
      }, DEVICE_SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    updateDebouncedDeviceNumber(normalizedDeviceNumber);

    return (): void => {
      updateDebouncedDeviceNumber.cancel();
    };
  }, [normalizedDeviceNumber, updateDebouncedDeviceNumber]);

  const companiesQuery = companyQueryClient.useGetCompaniesForDevice(debouncedDeviceNumber, {
    enabled: isSearchReady,
    retry: false,
  });

  const companies = companiesQuery.data ?? [];
  const isSearching = isSearchReady && (companiesQuery.isLoading || companiesQuery.isFetching);
  const hasResults = companies.length > 0;

  const handleCompanySelect = (company: CompanyDto): void => {
    if (selectedCompany.id === company.id) {
      localStorage.removeItem('selectedCompany');
      dispatch(CompanySelectionActionCreator.selectCompany({ id: null, name: null }));
      return;
    }

    localStorage.setItem('selectedCompany', JSON.stringify({ id: company.id, name: company.name }));
    dispatch(CompanySelectionActionCreator.selectCompany({ id: company.id, name: company.name }));
  };

  const handleClearSearch = (): void => {
    setSearchInput('');
  };

  return (
    <Header>
      <Container maxWidth="md" className="py-6 md:py-8">
        <Stack spacing={2.5}>
          <Paper elevation={0} className="rounded-xl border border-slate-200 p-5 md:p-6">
            <Stack spacing={2.5}>
              <Typography variant="h5" component="h1" className="font-semibold text-slate-900">
                {t('device_search_title') || 'Device company lookup'}
              </Typography>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <TextField
                  fullWidth
                  label={t('device_search_device_number') || 'Device number'}
                  placeholder={
                    t('device_search_device_number_placeholder') || 'Enter device number'
                  }
                  value={searchInput}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                    setSearchInput(event.target.value);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon className="text-slate-400" />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={t('device_search_clear') || 'Clear'}
                          edge="end"
                          onClick={handleClearSearch}
                          size="small"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                  helperText={
                    hasSearchValue
                      ? t('device_search_length_hint') ||
                        'Search starts automatically after you stop typing.'
                      : ' '
                  }
                  className="flex-1"
                />
              </div>
            </Stack>
          </Paper>

          {!hasSearchValue && (
            <Typography variant="body2" className="px-1 text-slate-500">
              {emptyPrompt}
            </Typography>
          )}

          {companiesQuery.isError && (
            <Alert severity="error" className="rounded-xl">
              {t('device_search_error') || 'Failed to load companies for this device number.'}
            </Alert>
          )}

          {isSearchReady && isSearching && (
            <Paper elevation={0} className="rounded-xl border border-slate-200 p-5">
              <div className="flex min-h-[120px] items-center justify-center gap-3 text-slate-600">
                <CircularProgress size={20} />
                <Typography variant="body2">{t('loading') || 'Loading'}</Typography>
              </div>
            </Paper>
          )}

          {isSearchReady && !isSearching && !companiesQuery.isError && hasResults && (
            <Paper elevation={0} className="rounded-xl bg-slate-200 p-px">
              <div className="overflow-hidden rounded-[inherit] bg-white">
                <DeviceSearchTable
                  companies={companies}
                  selectedCompanyId={selectedCompany.id}
                  onCompanySelect={handleCompanySelect}
                />
              </div>
            </Paper>
          )}

          {isSearchReady && !isSearching && !companiesQuery.isError && !hasResults && (
            <Alert severity="warning" className="rounded-xl">
              {t('device_search_no_matches') ||
                'No matching companies were found for this device number.'}
            </Alert>
          )}
        </Stack>
      </Container>
    </Header>
  );
};

export default DeviceSearch;
